import React, { useState } from 'react';
import { Button, TextField, Box, Typography, Snackbar, Alert } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Cookies from 'js-cookie';
import { encryptData } from '../utils/cryptoUtils.ts';
import { Slide } from 'react-awesome-reveal';
import { Particles } from '../components/Particles.tsx';
import { styled } from '@mui/material/styles';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { FaHome } from "react-icons/fa";

// Animáció a gombokhoz
const keyframes = `
@keyframes shine {
  0% { box-shadow: 0 0 10px #ff4081; }
  50% { box-shadow: 0 0 20px #ff80ab, 0 0 30px #ff4081; }
  100% { box-shadow: 0 0 10px #ff4081; }
}
`;

// Stílusos főgomb
const StyledActionButton = styled(Button)(({ theme }) => ({
  padding: '10px 24px',
  borderRadius: '30px',
  backgroundColor: '#ff4081',
  color: '#fff',
  fontWeight: 'bold',
  transition: '0.3s ease',
  boxShadow: '0 0 10px #ff4081',
  animation: 'shine 2s infinite alternate',
  '&:hover': {
    backgroundColor: '#f50057',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px #ff80ab',
  },
}));

// Másodlagos link gomb
const StyledSecondaryButton = styled(Button)(({ theme }) => ({
  mt: 2,
  textTransform: 'none',
  borderRadius: '20px',
  color: theme.palette.mode === 'dark' ? '#ff80ab' : '#f50057',
  '&:hover': {
    backgroundColor: 'rgba(255, 64, 129, 0.08)',
  },
}));

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState(false);
  const navigate = useNavigate();
  const { darkMode } = useThemeContext();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (password !== confirmPassword) {
      setSnackbarMessage("Passwords do not match!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
  
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
        credentials: 'include',
      });

      const data = await response.json();
  
      if (response.ok) {
        Cookies.set('token', data.token, { expires: 1 });
        localStorage.setItem('username', encryptData(username));
        localStorage.setItem('email', encryptData(email));
  
        setSnackbarMessage("Registration successful! Welcome aboard.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
  
        setTimeout(() => { navigate('/linklearning'); }, 1000);
      } else {
        // Itt kezeljük a hangzatosabb hibaüzenetet!
        if (response.status === 400 || response.status === 409) {
          setSnackbarMessage("Oops! This email or username is already taken. Try signing in instead!");
        } else {
          setSnackbarMessage(data.message || 'Registration failed');
        }
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred during registration. Please try again later.');
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <style>{keyframes}</style>
      <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
          <Particles />
        </div>
        
        <Navbar />
        
        <Slide triggerOnce direction="up">
          <Box
            component="form"
            onSubmit={handleRegister}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '450px',
              margin: '0 auto',
              padding: '30px',
              mt: '80px',
              backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              border: `1px solid ${darkMode ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
              boxShadow: darkMode ? '0 8px 32px rgba(255, 0, 0, 0.15)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          > 
            <Button 
                component={Link} 
                to="/" 
                sx={{ mb: 2, color: darkMode ? '#fff' : 'primary.main', alignSelf: 'center' }}
            >
              <FaHome size="2em" style={{ marginRight: '10px' }} />
              Home
            </Button>

            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', color: darkMode ? '#fff' : '#333' }}>
              Create Account
            </Typography>

            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="dense"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              margin="dense"
              value={email}
              error={emailError}
              helperText={emailError ? "Please enter a valid email address." : ""}
              onChange={(e) => {
                const value = e.target.value;
                setEmail(value);
                const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                setEmailError(value ? !emailRegex.test(value) : false);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              label="Password"
              variant="outlined"
              fullWidth
              margin="dense"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              label="Confirm Password"
              variant="outlined"
              fullWidth
              margin="dense"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <StyledActionButton type="submit" variant="contained" sx={{ mt: 3 }}>
              Sign Up
            </StyledActionButton>

            <StyledSecondaryButton component={Link} to="/login" sx={{ mt: 2 }}>
              Already have an account? Sign In!
            </StyledSecondaryButton>
          </Box>
        </Slide>
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '12px' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default Register;